// Chapter 20 - Engineering Emergent Learning with Nonequivalence Relations
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 20 (Critchfield & Rehfeldt).
// All content traced to source-pdfs/Chapter-20-Engineering-Emergent-Learning-with-Nonequivalence-Relations-9.pdf

import type { ChapterModule } from "./types";

const chapter20Module: ChapterModule = {
  meta: {
    id: "20",
    number: 20,
    title: "Engineering Emergent Learning with Nonequivalence Relations",
    subtitle: "Relational frames, derived relational responding",
    part: "Part 8 - Developing New Behavior",
    estMinutes: 70,
    bacb: ["G-11"],
  },
  curated: true,
  overview: [
    "Chapter 20 (Critchfield & Rehfeldt) extends emergent-learning beyond 'same as' (Chapter 19) to NONEQUIVALENCE RELATIONS such as opposition, comparison (more/less), hierarchy, spatial, temporal, causal, distinction, and deictic relations. The chapter introduces the vocabulary of Relational Frame Theory (RFT): MUTUAL ENTAILMENT (the converse), COMBINATORIAL ENTAILMENT (derived relations through indirect linkages), and TRANSFORMATION OF FUNCTION (functions acquired by one stimulus shift to others according to the relation).",
    "Applications include teaching categorical knowledge (hierarchical relations), shaping self-concept and social skills (deictic relations and perspective shifting), boosting intellectual development through systematic relational training, and improving general well-being through acceptance and commitment therapy (ACT), which uses relational responding to undermine the control of behavior by problematic private events.",
  ],
  objectives: [
    { id: "G-11", text: "Use derived stimulus relations (including nonequivalence) to engineer emergent learning." },
  ],
  coreConcepts: [
    {
      title: "Stimulus relations beyond equivalence",
      body: [
        "Stimulus relations are arbitrary associations: how one stimulus is responded to is intertwined with how others are responded to. Stimulus equivalence (Ch 19) is one variety; opposition, comparison, hierarchy, spatial, temporal, causal, distinction, and deictic relations are equally integral to everyday human behavior.",
      ],
    },
    {
      title: "Vocabulary: mutual and combinatorial entailment",
      body: [
        "MUTUAL ENTAILMENT subsumes equivalence's SYMMETRY: the converse of an A-to-B relation. If 'A is greater than B,' the entailed converse is 'B is less than A.' COMBINATORIAL ENTAILMENT subsumes equivalence's TRANSITIVITY: any derived relation in which the function of stimuli depends on other stimuli they are indirectly related to.",
      ],
    },
    {
      title: "Transformation of function",
      body: [
        "Transformation of function generalizes equivalence's TRANSFER OF FUNCTION. Stimuli in a relational class do NOT all acquire the SAME functions; instead, functions acquired by any stimulus depend on the functions acquired by others in the class (per the relation involved).",
      ],
    },
    {
      title: "Types of nonequivalence relations",
      body: [
        "DISTINCTION RELATIONS: stimuli are judged as different. OPPOSITION RELATIONS: stimuli are opposites. COMPARISON RELATIONS: stimuli differ along a definable dimension (more/less, taller/shorter). HIERARCHICAL RELATIONS: category membership (dog -> mammal -> animal). SPATIAL/TEMPORAL RELATIONS: above/below, before/after. CAUSAL RELATIONS: A causes B. DEICTIC RELATIONS: relative perspective (I/you, here/there, now/then) - foundational to self-concept and social cognition.",
      ],
    },
    {
      title: "Contextual cues for the relational class",
      body: [
        "Because there are many ways to relate the same stimuli, contextual cues signal which way of relating is appropriate. Explicit contextual stimuli are recommended as part of applied interventions focusing on derived stimulus relations.",
      ],
    },
    {
      title: "Relational Frame Theory (RFT) foundations",
      body: [
        "Most nonequivalence research is guided by RFT, which assumes (a) verbal processes are bound up in derived stimulus relations and (b) with enough reinforced experience across multiple exemplars, people develop a higher-order ability to derive relations WITHOUT further reinforced experience. This generalized capability is called ARBITRARILY APPLICABLE RELATIONAL RESPONDING.",
      ],
    },
    {
      title: "Verbal experience and derived relations",
      body: [
        "For some learners, baseline relations for stimulus classes can be built simply by TELLING them about the relations. This implies that verbal experience is functionally similar to the largely nonverbal MTS experience in equivalence protocols.",
      ],
    },
    {
      title: "Applications: categorical knowledge, self-concept, intellectual development",
      body: [
        "Establishing hierarchical relations produces effects on categorization. Deictic relations underlie self-concept and PERSPECTIVE SHIFTING (experiencing the world as another person would). Systematically training relations has been found to BOOST scores on standardized intelligence tests.",
      ],
    },
    {
      title: "Well-being and Acceptance and Commitment Therapy (ACT)",
      body: [
        "'Well-being behaviors' are those on which a person's most-valued reinforcers are contingent. Many challenges to psychological well-being involve constraining of well-being behaviors by private events (emotional responses, self-generated rules). ACT, partly based on RFT and derived stimulus relations, uses verbally guided exercises to undermine problematic verbal control of behavior, lessening symptoms of clinical disorders and improving caregiver well-being.",
      ],
    },
    {
      title: "Behavioral inflexibility and rule-governed behavior",
      body: [
        "Rules generated by self or others can produce BEHAVIORAL INFLEXIBILITY when they dominate contact with contingencies. Loosening rigid rule control is a target of ACT-style interventions and broader RFT-based programs.",
      ],
    },
  ],
  glossary: [
    { term: "Derived stimulus relations", definition: "Stimulus relations that emerge from a learning history without being directly trained.", sourceHint: "derived relations (Key Terms list)." },
    { term: "Arbitrarily applicable relational responding", definition: "A generalized repertoire that allows new relational classes to form without new reinforced experience (RFT).", sourceHint: "what relational frame theory calls arbitrarily applicable relational responding, a generalized repertoire that allows new classes to form without new reinforced experience." },
    { term: "Mutual entailment", definition: "The converse of any A-to-B relation; subsumes equivalence's symmetry.", sourceHint: "What's called symmetry in the stimulus equivalence literature is subsumed under the term mutual entailment, which means the converse of any A-to-B relation." },
    { term: "Combinatorial entailment", definition: "Derived relations in which the function of stimuli depends on indirect linkages; subsumes equivalence's transitivity.", sourceHint: "What's called transitivity in the stimulus equivalence literature is subsumed under the term combinatorial entailment." },
    { term: "Transformation of function", definition: "Functions acquired by stimuli in a relational class depend on the functions acquired by others, per the relation involved (subsumes transfer of function).", sourceHint: "What's called transfer of function in the stimulus equivalence literature is subsumed under the term transformation of function." },
    { term: "Relational frame", definition: "A higher-order operant class of relational responding, established by multiple-exemplar training (RFT).", sourceHint: "relational frame (Key Terms list)." },
    { term: "Relational frame theory (RFT)", definition: "Theoretical framework that assumes verbal processes are bound up in derived stimulus relations and that relational frames are higher-order operants established through multiple-exemplar training.", sourceHint: "Most research on nonequivalence relations has been guided by relational frame theory." },
    { term: "Multiple-exemplar training", definition: "Reinforced experience across many exemplars of a relation, producing generalizable relational ability.", sourceHint: "providing reinforced multiple-exemplar experience has been shown to create generalizable relational abilities." },
    { term: "Distinction relations", definition: "Relations in which stimuli are judged as different.", sourceHint: "Distinction relations involve relative stimuli being judged as different." },
    { term: "Opposition relations", definition: "Relations in which stimuli are opposites.", sourceHint: "In opposition relations, stimuli are opposites." },
    { term: "Comparison relations", definition: "Relations in which stimuli differ along a definable dimension (e.g., more/less).", sourceHint: "In comparison relations, stimuli differ along some definable dimension." },
    { term: "Hierarchical relations", definition: "Relations of category membership (member, subclass, superclass).", sourceHint: "Establishing a type of nonequivalence relation called hierarchical relations produces several effects seen in cognitive psychology studies of categorization." },
    { term: "Spatial relations", definition: "Relations involving spatial juxtaposition (above/below, inside/outside).", sourceHint: "Stimuli can also be related in terms of spatial or temporal juxtaposition." },
    { term: "Causal relations", definition: "Relations in which one stimulus is treated as causing another.", sourceHint: "in terms of cause-effect relations." },
    { term: "Deictic relations", definition: "Relations defined by relative perspective (I/you, here/there, now/then) - foundational to self-concept and social repertoires.", sourceHint: "A type of nonequivalence relation called deictic relations may underlie important self-related and social repertoires." },
    { term: "Perspective shifting", definition: "Experiencing the world as another person would; a deictic-relations skill.", sourceHint: "perspective shifting, in which an individual experiences the world as another person would." },
    { term: "Behavioral inflexibility", definition: "Rigid responding under verbal rule control that prevents contact with reinforcing contingencies.", sourceHint: "behavioral inflexibility (Key Terms list)." },
    { term: "Acceptance and commitment therapy (ACT)", definition: "Behavior therapy partly based on RFT that uses verbally guided exercises to loosen problematic verbal control and promote well-being.", sourceHint: "Acceptance and commitment therapy is a behavior therapy based partly on relational frame theory and research on derived stimulus relations." },
    { term: "Rule-governed behavior", definition: "Behavior controlled by verbal statements describing contingencies; can produce inflexibility if rules dominate contact with contingencies.", sourceHint: "rule-governed behavior (Key Terms list)." },
  ],
  procedures: [
    {
      title: "Identify a nonequivalence relation in a sample of behavior",
      intro: "Use the RFT vocabulary to label what is happening.",
      steps: [
        { title: "Specify the stimuli", detail: "List the two or more stimuli involved." },
        { title: "Specify the relation", detail: "Is it distinction, opposition, comparison, hierarchical, spatial, temporal, causal, or deictic?" },
        { title: "Identify the contextual cue", detail: "What signals which relation applies (e.g., the word 'opposite', 'bigger')?" },
        { title: "Probe mutual entailment", detail: "Does the converse hold without further training?" },
        { title: "Probe combinatorial entailment", detail: "Does a relation between indirectly linked stimuli emerge?" },
        { title: "Probe transformation of function", detail: "Do functions transform across stimuli per the relation?" },
      ],
    },
    {
      title: "Use multiple-exemplar training to build a relational repertoire",
      intro: "Generalize across exemplars to produce arbitrarily applicable relational responding.",
      steps: [
        { title: "Pick a relation", detail: "E.g., comparison ('bigger than')." },
        { title: "Train multiple exemplars", detail: "Use many pairs of stimuli that exemplify the relation; reinforce correct relational responding." },
        { title: "Probe with novel stimuli", detail: "Test the same relation with stimuli never used during training." },
        { title: "Confirm generalization", detail: "If the learner accurately applies the relation to novel stimuli, an arbitrarily applicable relational responding repertoire is in place." },
      ],
    },
    {
      title: "Apply deictic relation training for perspective taking",
      intro: "I/you, here/there, now/then - foundational to social cognition.",
      steps: [
        { title: "Train I/you", detail: "Reinforce responses that correctly attribute experiences to self vs other." },
        { title: "Train here/there", detail: "Reinforce responses involving spatial perspective." },
        { title: "Train now/then", detail: "Reinforce temporal-perspective responses (e.g., what was true earlier vs now)." },
        { title: "Probe combinations", detail: "Test perspective shifts that combine all three deictic frames." },
      ],
    },
  ],
  examples: [
    { scenario: "Learner is taught 'A is bigger than B' and 'B is bigger than C'; later, without training, learner says 'A is bigger than C' and 'C is smaller than A.'",
      application: "Combinatorial entailment (A-C) and mutual entailment (smaller). A nonequivalence (comparison) relation produces emergent responses.",
      sourceHint: "What's called transitivity in the stimulus equivalence literature is subsumed under the term combinatorial entailment." },
    { scenario: "After learning that dogs are mammals and mammals are animals, the learner sorts pictures hierarchically and infers properties (all animals breathe; therefore dogs breathe).",
      application: "Hierarchical relations underwriting category knowledge and inferences.",
      sourceHint: "Establishing a type of nonequivalence relation called hierarchical relations produces several effects seen in cognitive psychology studies of categorization." },
    { scenario: "A child describes how their friend might feel if their toy broke, despite never having had the same experience.",
      application: "Deictic relations and perspective shifting.",
      sourceHint: "deictic relations may underlie important self-related and social repertoires, a primary example of which is perspective shifting." },
    { scenario: "An adult struggling with intrusive thoughts uses ACT exercises to defuse the verbal control of those thoughts and resume valued action.",
      application: "ACT leverages RFT-based exercises to undermine the control of behavior by problematic private events.",
      sourceHint: "Acceptance and commitment therapy is a behavior therapy based partly on relational frame theory and research on derived stimulus relations." },
  ],
  comparisons: [
    {
      title: "Equivalence (Sidman) vs Relational frames (RFT)",
      left:  { label: "Stimulus equivalence", points: [
        "Same-as relation only.",
        "Symmetry + transitivity + reflexivity.",
        "Transfer of function across class members.",
      ]},
      right: { label: "Relational frames", points: [
        "Many relation types (opposite, more/less, hierarchical, deictic, etc.).",
        "Mutual + combinatorial entailment.",
        "Transformation of function per the relation involved.",
      ]},
    },
    {
      title: "Mutual entailment vs Combinatorial entailment",
      left:  { label: "Mutual entailment", points: [
        "Direct converse of an A-to-B relation.",
        "E.g., 'A is bigger than B' -> 'B is smaller than A.'",
      ]},
      right: { label: "Combinatorial entailment", points: [
        "Derived relations through indirect linkages.",
        "E.g., 'A is bigger than B' + 'B is bigger than C' -> 'A is bigger than C.'",
      ]},
    },
    {
      title: "Distinction vs Opposition vs Comparison",
      left:  { label: "Distinction / Opposition", points: [
        "Distinction: stimuli are different.",
        "Opposition: stimuli are opposites along a dimension.",
      ]},
      right: { label: "Comparison", points: [
        "Stimuli differ along a definable dimension (more/less).",
        "Foundation for ordinal and quantitative skills.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Stimulus equivalence is the only emergent relational repertoire.",
      reality: "Equivalence is one of many derived relations. Opposition, comparison, hierarchy, spatial, temporal, causal, distinction, and deictic relations are equally important.",
      sourceHint: "Stimulus equivalence is only one of a wide variety of stimulus relations.",
    },
    {
      myth: "Symmetry and transitivity describe all derived relations.",
      reality: "For nonequivalence relations, the analogues are mutual entailment and combinatorial entailment, which are more general.",
      sourceHint: "Key ways of talking about stimulus equivalence do not work for nonequivalence relations, so a substitute vocabulary is needed.",
    },
    {
      myth: "Functions transfer identically across all members of a relational class.",
      reality: "TRANSFORMATION OF FUNCTION is per-relation; members do NOT all acquire the same functions but acquire transformed functions per the relation involved.",
      sourceHint: "transformation of function. This means that the stimuli in a relational class do not all acquire the same behavioral functions; however, the functions acquired by any stimulus depend on the functions acquired by others in the class.",
    },
    {
      myth: "Relational frames are intrinsic cognitive structures.",
      reality: "Relational frames are higher-order OPERANTS established by reinforced multiple-exemplar training; they are not innate structures.",
      sourceHint: "given enough reinforced experience with multiple exemplars, people develop a higher-order ability to derive relations among sets of stimuli.",
    },
    {
      myth: "ACT abandons behavior analysis to focus on private events.",
      reality: "ACT IS behavior analytic: it uses verbally guided exercises grounded in RFT and derived stimulus relations to undermine problematic verbal control and promote well-being behaviors.",
      sourceHint: "Acceptance and commitment therapy is a behavior therapy based partly on relational frame theory and research on derived stimulus relations.",
    },
  ],
  examChecklist: [
    "Define derived stimulus relations beyond equivalence.",
    "Define mutual entailment, combinatorial entailment, and transformation of function.",
    "Name and briefly describe seven types of nonequivalence relations.",
    "Distinguish equivalence vocabulary (symmetry, transitivity, transfer of function) from RFT vocabulary.",
    "Define arbitrarily applicable relational responding.",
    "Describe multiple-exemplar training and one example.",
    "Define deictic relations and perspective shifting.",
    "Explain how hierarchical relations underwrite categorical knowledge.",
    "Describe ACT and how it leverages RFT to address private-event control.",
    "Define behavioral inflexibility and rule-governed behavior.",
  ],
  flashcardTerms: [
    "Derived stimulus relations", "Arbitrarily applicable relational responding",
    "Mutual entailment", "Combinatorial entailment", "Transformation of function",
    "Relational frame", "Relational frame theory (RFT)", "Multiple-exemplar training",
    "Distinction relations", "Opposition relations", "Comparison relations",
    "Hierarchical relations", "Spatial relations", "Causal relations",
    "Deictic relations", "Perspective shifting",
    "Behavioral inflexibility", "Acceptance and commitment therapy (ACT)",
    "Rule-governed behavior",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which term subsumes symmetry under RFT vocabulary?",
      options: [
        { label: "A", text: "Mutual entailment" },
        { label: "B", text: "Combinatorial entailment" },
        { label: "C", text: "Transformation of function" },
        { label: "D", text: "Distinction" },
      ],
      answer: "A",
      explanation: "Mutual entailment is the RFT counterpart of symmetry - the converse of an A-to-B relation.",
      topic: "RFT vocabulary",
      sourceHint: "What's called symmetry in the stimulus equivalence literature is subsumed under the term mutual entailment.",
    },
    {
      id: "Q2",
      question: "If a learner is taught 'A is bigger than B' and 'B is bigger than C' and then derives 'A is bigger than C,' this is:",
      options: [
        { label: "A", text: "Mutual entailment" },
        { label: "B", text: "Combinatorial entailment" },
        { label: "C", text: "Reflexivity" },
        { label: "D", text: "Distinction" },
      ],
      answer: "B",
      explanation: "Combinatorial entailment: derived relation through indirectly linked stimuli (the RFT analogue of transitivity).",
      topic: "Combinatorial entailment",
      sourceHint: "What's called transitivity in the stimulus equivalence literature is subsumed under the term combinatorial entailment.",
    },
    {
      id: "Q3",
      question: "Functions acquired by stimuli in a relational class:",
      options: [
        { label: "A", text: "Are identical for every class member" },
        { label: "B", text: "Depend on the functions acquired by others, transformed per the relation involved" },
        { label: "C", text: "Cannot transfer across stimuli" },
        { label: "D", text: "Are independent of training history" },
      ],
      answer: "B",
      explanation: "Transformation of function: functions are NOT identical across class members; each member's function depends on the relation and other members.",
      topic: "Transformation of function",
      sourceHint: "transformation of function. This means that the stimuli in a relational class do not all acquire the same behavioral functions; however, the functions acquired by any stimulus depend on the functions acquired by others in the class.",
    },
    {
      id: "Q4",
      question: "Deictic relations underwrite which big-picture construct?",
      options: [
        { label: "A", text: "Categorical knowledge" },
        { label: "B", text: "Self-concept and perspective shifting" },
        { label: "C", text: "Causal reasoning" },
        { label: "D", text: "Operant conditioning" },
      ],
      answer: "B",
      explanation: "Deictic relations (I/you, here/there, now/then) underlie self-concept and the ability to take another's perspective.",
      topic: "Deictic relations",
      sourceHint: "A type of nonequivalence relation called deictic relations may underlie important self-related and social repertoires, a primary example of which is perspective shifting.",
    },
    {
      id: "Q5",
      question: "Arbitrarily applicable relational responding is best described as:",
      options: [
        { label: "A", text: "A generalized repertoire that allows new relational classes to form without new reinforced experience" },
        { label: "B", text: "A reflex tied to a specific stimulus" },
        { label: "C", text: "A response only emitted in the presence of an SD" },
        { label: "D", text: "An unconditioned relational response" },
      ],
      answer: "A",
      explanation: "RFT views arbitrarily applicable relational responding as a higher-order, generalized operant arising from multiple-exemplar training.",
      topic: "RFT",
      sourceHint: "what relational frame theory calls arbitrarily applicable relational responding, a generalized repertoire that allows new classes to form without new reinforced experience.",
    },
    {
      id: "Q6",
      question: "ACT (Acceptance and Commitment Therapy) is based partly on:",
      options: [
        { label: "A", text: "Stimulus equivalence only" },
        { label: "B", text: "Operant conditioning only" },
        { label: "C", text: "Relational frame theory and derived stimulus relations" },
        { label: "D", text: "Classical conditioning" },
      ],
      answer: "C",
      explanation: "ACT draws on RFT and research on derived stimulus relations (including nonequivalence) to undermine problematic verbal control.",
      topic: "ACT",
      sourceHint: "Acceptance and commitment therapy is a behavior therapy based partly on relational frame theory and research on derived stimulus relations (including nonequivalence relations).",
    },
    {
      id: "Q7",
      question: "Systematic training of relational repertoires has been found to:",
      options: [
        { label: "A", text: "Have no impact on standardized assessments" },
        { label: "B", text: "Boost scores on standardized intelligence tests" },
        { label: "C", text: "Decrease verbal repertoires" },
        { label: "D", text: "Prevent perspective shifting" },
      ],
      answer: "B",
      explanation: "Studies show that systematically training derived stimulus relations can boost scores on standardized intelligence tests.",
      topic: "Intellectual development",
      sourceHint: "Importantly, systematically training stimulus relations has been found to boost scores on standardized intelligence tests.",
    },
  ],
  reviewPrompts: [
    "List seven nonequivalence relations and give one example of each.",
    "Translate the equivalence vocabulary (symmetry, transitivity, transfer of function) into RFT vocabulary.",
    "Design a multiple-exemplar training program for the 'opposite' relation.",
    "Describe how deictic relation training could support perspective-taking in a clinical case.",
    "Explain how hierarchical relations support category-based reasoning.",
    "Discuss how ACT uses verbally guided exercises grounded in RFT.",
  ],
  recap: [
    "Stimulus equivalence is one of many derived stimulus relations. Nonequivalence relations include distinction, opposition, comparison, hierarchy, spatial, temporal, causal, and deictic relations.",
    "RFT vocabulary generalizes the equivalence concepts: mutual entailment (symmetry analogue), combinatorial entailment (transitivity analogue), and transformation of function (transfer of function analogue). Relational frames are higher-order operants established by multiple-exemplar training.",
    "Applications include categorical knowledge (hierarchical relations), self-concept and perspective shifting (deictic relations), intellectual development gains from systematic relational training, and ACT-based interventions that loosen problematic verbal control to promote well-being.",
  ],
  sourceExcerpts: [
    {
      heading: "Beyond equivalence",
      text: "Stimulus equivalence is only one of a wide variety of stimulus relations. Not all stimuli are related via 'same as,' and nonequivalence relations are integral to many everyday experiences.",
    },
    {
      heading: "RFT vocabulary",
      text: "What's called symmetry in the stimulus equivalence literature is subsumed under the term mutual entailment, which means the converse of any A-to-B relation. What's called transitivity in the stimulus equivalence literature is subsumed under the term combinatorial entailment. What's called transfer of function in the stimulus equivalence literature is subsumed under the term transformation of function.",
    },
    {
      heading: "Deictic relations and self-concept",
      text: "A type of nonequivalence relation called deictic relations may underlie important self-related and social repertoires, a primary example of which is perspective shifting, in which an individual experiences the world as another person would.",
    },
    {
      heading: "ACT and well-being",
      text: "Acceptance and commitment therapy is a behavior therapy based partly on relational frame theory and research on derived stimulus relations (including nonequivalence relations). It focuses on promoting general well-being. The therapy has been shown to lessen symptoms of certain clinical disorders and to enhance performance in nonclinical situations.",
    },
  ],
};

export default chapter20Module;
